import { useMemo } from 'react';
import clsx from 'clsx';
import type { MaBrandRow } from '../../services/microbrandAtlas.types';
import { Badge } from '../common/Badge';
import { BrandLogo } from '../common/BrandLogo';
import { Button } from '../common/Button';
import { COUNTRY_WARNING } from './fieldSpec';
import { describeMatch, type ProposalCategory, type ProposalMatch } from './proposalMatch';
import {
  patchProposalsUi,
  useSessionCache,
  type ListStatusFilter,
} from './sessionCache';
import { SessionStatusBadge, useSessionStatuses } from './sessionStatus';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const STATUS_CHIPS: { id: ListStatusFilter; label: string }[] = [
  { id: 'unmatched', label: 'Unmatched' },
  { id: 'matched', label: 'Matched' },
  { id: 'session-created', label: 'Created this session' },
  { id: 'session-updated', label: 'Updated this session' },
  { id: 'session-dismissed', label: 'Dismissed' },
  { id: 'all', label: 'All' },
];

/** MA's "normalised" platform column still mixes case; fold it for filtering. */
export function foldPlatform(value: string | null | undefined): string | null {
  const folded = value?.trim().toLowerCase();
  return folded ? folded : null;
}

interface ProposalsListProps {
  rows: MaBrandRow[];
  /** Match per MA slug, computed once by the page for the whole list. */
  matches: ReadonlyMap<string, ProposalMatch>;
  /** The filter columns failed to load: the two filters that need them are off. */
  filtersUnavailable: boolean;
  onOpen: (slug: string) => void;
}

/**
 * The MA catalogue as a filterable table. All rows are in memory, so every
 * filter, the search and the paging are plain client-side predicates.
 */
export function ProposalsList({ rows, matches, filtersUnavailable, onOpen }: ProposalsListProps) {
  const ui = useSessionCache((s) => s.ui);
  const { statuses } = useSessionStatuses();

  const counts = useMemo(() => {
    const tally: Record<ProposalCategory, number> = {
      unmatched: 0,
      matched: 0,
      'session-created': 0,
      'session-updated': 0,
      'session-dismissed': 0,
    };
    for (const row of rows) {
      const category = matches.get(row.slug)?.category;
      if (category) tally[category] += 1;
    }
    return tally;
  }, [rows, matches]);

  const platforms = useMemo(() => {
    const tally = new Map<string, number>();
    for (const row of rows) {
      const platform = foldPlatform(row.ecommerce_platform);
      if (platform) tally.set(platform, (tally.get(platform) ?? 0) + 1);
    }
    return Array.from(tally.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = ui.search.trim().toLowerCase();
    return rows.filter((row) => {
      if (ui.statusFilter !== 'all' && matches.get(row.slug)?.category !== ui.statusFilter) {
        return false;
      }
      if (ui.platformFilter && foldPlatform(row.ecommerce_platform) !== ui.platformFilter) {
        return false;
      }
      if (ui.founderManagedOnly && row.founder_managed !== true) return false;
      if (needle) {
        const haystack = [row.name, row.slug, row.country, row.city, row.founder_name]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, matches, ui.search, ui.statusFilter, ui.platformFilter, ui.founderManagedOnly]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / ui.pageSize));
  const page = Math.min(ui.page, pageCount);
  const pageRows = filtered.slice((page - 1) * ui.pageSize, page * ui.pageSize);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_CHIPS.map((chip) => {
          const count = chip.id === 'all' ? rows.length : counts[chip.id];
          return (
            <button
              key={chip.id}
              type="button"
              aria-pressed={ui.statusFilter === chip.id}
              onClick={() => patchProposalsUi({ statusFilter: chip.id, page: 1 })}
              className={clsx(
                'rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                ui.statusFilter === chip.id
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
              )}
            >
              {chip.label} <span className="opacity-75">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <label htmlFor="ma-search" className="mb-1 block text-sm font-medium text-gray-700">
            Search
          </label>
          <input
            id="ma-search"
            type="search"
            value={ui.search}
            onChange={(e) => patchProposalsUi({ search: e.target.value, page: 1 })}
            placeholder="Name, slug, country, city or founder…"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label htmlFor="ma-platform" className="mb-1 block text-sm font-medium text-gray-700">
            Platform
          </label>
          <select
            id="ma-platform"
            value={ui.platformFilter ?? ''}
            disabled={filtersUnavailable}
            onChange={(e) => patchProposalsUi({ platformFilter: e.target.value || null, page: 1 })}
            className="rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-500 disabled:bg-gray-100"
          >
            <option value="">All platforms</option>
            {platforms.map(([platform, count]) => (
              <option key={platform} value={platform}>
                {platform} ({count})
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 pb-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={ui.founderManagedOnly}
            disabled={filtersUnavailable}
            onChange={(e) => patchProposalsUi({ founderManagedOnly: e.target.checked, page: 1 })}
            className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
          />
          Founder-managed only
        </label>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 font-medium">Brand</th>
              <th className="px-3 py-2 font-medium">
                Country{' '}
                <span className="normal-case text-amber-700" title={COUNTRY_WARNING}>
                  ⚠
                </span>
              </th>
              <th className="px-3 py-2 font-medium">City</th>
              <th className="px-3 py-2 font-medium">Founded</th>
              <th className="px-3 py-2 font-medium">Founder</th>
              <th className="px-3 py-2 font-medium">Platform</th>
              <th className="px-3 py-2 font-medium">WK match</th>
              <th className="px-3 py-2 font-medium">Session</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                  No brands match these filters.
                </td>
              </tr>
            )}
            {pageRows.map((row) => {
              const match = matches.get(row.slug);
              const platform = foldPlatform(row.ecommerce_platform);
              return (
                <tr
                  key={row.id}
                  onClick={() => onOpen(row.slug)}
                  className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                      <BrandLogo src={row.logo_url} size={32} lazy />
                      <div className="min-w-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpen(row.slug);
                          }}
                          className="block truncate text-left font-medium text-brand-700 hover:underline"
                        >
                          {row.name}
                        </button>
                        <div className="truncate text-xs text-gray-500">{row.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-500" title={COUNTRY_WARNING}>
                    {row.country || '—'}
                  </td>
                  <td className="px-3 py-2">{row.city || '—'}</td>
                  <td className="px-3 py-2">{row.founded_year ?? '—'}</td>
                  <td className="max-w-[12rem] truncate px-3 py-2" title={row.founder_name ?? undefined}>
                    {row.founder_name || '—'}
                    {row.founder_managed && (
                      <Badge variant="info" className="ml-1.5">
                        managed
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {platform ? (
                      <Badge variant={platform === 'shopify' ? 'success' : 'default'}>{platform}</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {match?.wkBrand ? (
                      <div>
                        <div className="font-medium text-gray-800">{match.wkBrand.name}</div>
                        <div
                          className={clsx(
                            'text-xs',
                            match.needsConfirm ? 'text-amber-700' : 'text-gray-500',
                          )}
                        >
                          {describeMatch(match)}
                          {match.needsConfirm && ' — confirm'}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <SessionStatusBadge status={statuses.get(row.slug)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
        <div>
          {filtered.length === 0
            ? 'Showing 0'
            : `Showing ${(page - 1) * ui.pageSize + 1}–${Math.min(page * ui.pageSize, filtered.length)}`}{' '}
          of {filtered.length}
          {filtered.length !== rows.length && ` (filtered from ${rows.length})`}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="ma-page-size">Rows</label>
          <select
            id="ma-page-size"
            value={ui.pageSize}
            onChange={(e) => patchProposalsUi({ pageSize: Number(e.target.value), page: 1 })}
            className="rounded-lg border border-gray-300 px-2 py-1"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => patchProposalsUi({ page: page - 1 })}
          >
            Previous
          </Button>
          <span>
            Page {page} of {pageCount}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => patchProposalsUi({ page: page + 1 })}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
